import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  BillingActionsDetailsState,
  BillingActionsDetailsQStringParameters,
} from './slice/types';
import { useBillingActionsDetailsSlice } from './slice/index';
import { PageWrapper } from 'app/Layout/FrontendLayout/components/PageWrapper';
import { translations } from 'locales/translations';
import {
  selectBillingActionsDetailsProcessing,
  selectCreateBillingActionsDetails,
  selectCreateBillingActionsDetailsCompleted,
  selectUpdateBillingActionsDetails,
  selectUpdateBillingActionsDetailsCompleted,
  //selectCreateBillingActionsDetailsHasError,
  //selectUpdateBillingActionsDetailsHasError,
} from './slice/selectors';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  selectConfirmApproved,
  selectConfirmRejected,
  selectContinueState,
  selectContinueToLink,
  selectHasNotSavedChanges,
} from 'app/Layout/FrontendLayout/slice/selectors';
import { useLayoutSlice } from 'app/Layout/FrontendLayout/slice';
import { push } from 'connected-react-router';
import Box from '@material-ui/core/Box';
import { Progress } from 'app/components/LoadingIndicator';
import { BillingActionsForm } from './BillingActionsForm/index';
import { useEffectOnMount } from 'app/hooks/useEffectOnMount';
import { selectAuthenticatedUser, selectPublicUrl } from 'app/slice/selectors';
import { Roles } from 'app/slice/types';
import { Beforeunload } from 'react-beforeunload';
import { buildURL, toQueryString } from 'utils/url-utils';
import { Icon } from 'app/components/BasicIcons/FontAwesome';
import { ActionRenderer } from 'app/Layout/FrontendLayout/components/PageWrapper/PageActions/ActionRender';
import { SaveFormButton } from './Actions/BillingActions_Actions';
import { Button } from 'app/components/BasicButtons/Button';
import { useAppSettingsSlice } from 'app/slice';
import { SidePanelContentProps } from 'app/Layout/FrontendLayout/slice/type';

export interface BillingActionsDetailsProps extends SidePanelContentProps {
  queryParams: BillingActionsDetailsQStringParameters;
}

export function BillingActionsDetails(
  props: BillingActionsDetailsProps,
): JSX.Element {
  //PROPS
  const { queryParams, useSidePanel } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //PERMISSIONS
  const User = useSelector(selectAuthenticatedUser);
  const IsAdmin =
    User !== undefined && User.Roles.includes(Roles.Administrators);

  //SLICES
  const { actions } = useBillingActionsDetailsSlice();
  const { actions: layoutActions } = useLayoutSlice();
  const { actions: appSettingsActions } = useAppSettingsSlice();

  //SELECTORS
  const publicUrl = useSelector(selectPublicUrl);
  //====================================================================
  const hasUnsavedChanges = useSelector(selectHasNotSavedChanges, shallowEqual);
  const continueLink = useSelector(selectContinueToLink);
  const loading = useSelector(selectBillingActionsDetailsProcessing);
  //====================================================================
  const confirmRejected = useSelector(selectConfirmRejected);
  const confirmApproved = useSelector(selectConfirmApproved);
  const continueToOther = useSelector(selectContinueState);
  //====================================================================
  const requestCreateCompleted = useSelector(
    selectCreateBillingActionsDetailsCompleted,
  );
  const requestUpdateCompleted = useSelector(
    selectUpdateBillingActionsDetailsCompleted,
  );
  //====================================================================
  //const hasCreateError = useSelector(selectCreateBillingActionsDetailsHasError);
  //const hasUpdateError = useSelector(selectUpdateBillingActionsDetailsHasError);
  //const hasError = hasCreateError || hasUpdateError;
  //====================================================================
  const createModel = useSelector(
    selectCreateBillingActionsDetails,
    shallowEqual,
  );
  const editModel = useSelector(
    selectUpdateBillingActionsDetails,
    shallowEqual,
  );
  const baseModel = createModel || editModel;
  //====================================================================

  //STATES
  const [edit, setEdit] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [requestCompleted, setRequestCompleted] = useState<boolean | undefined>(
    undefined,
  );

  //REF
  const submitFormRef = useRef<any>(null);

  //FUNCTIONS
  const bindSubmitForm = useCallback(submitForm => {
    submitFormRef.current = submitForm;
  }, []);

  const handleSubmitForm = (e: any) => {
    if (submitFormRef.current) {
      submitFormRef.current(e);
    }
  };

  const handleSaveClick = (e: any) => {
    handleSubmitForm(e);
  };

  const handleSubmit = useCallback(
    (values: BillingActionsDetailsState) => {
      setBusy(true);
      if (edit) {
        dispatch(
          actions.updateBillingActionsDetails({
            current: values,
            original: baseModel,
          }),
        );
      } else {
        let value = values as BillingActionsDetailsState;
        console.debug('Data: ', value);
        dispatch(actions.createBillingActionsDetails(values));
      }
    },
    [actions, baseModel, dispatch, edit],
  );

  const handleCancelClick = useCallback(() => {
    if (useSidePanel) {
      dispatch(layoutActions.resetSidePanel());
    } else {
      dispatch(push('/BillingActions'));
    }
    if (edit) {
      dispatch(actions.resetUpdateBillingActionsDetailsState());
    } else {
      dispatch(actions.resetCreateBillingActionsDetailsState());
    }
  }, [actions, dispatch, edit, layoutActions, useSidePanel]);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      dispatch(layoutActions.setConfirmOpen(true));
    } else {
      handleCancelClick();
    }
  };

  //USE EFFECT ON MOUNT
  useEffectOnMount(() => {
    if (
      queryParams.id !== undefined &&
      !isNaN(parseInt(queryParams.id || ''))
    ) {
      dispatch(actions.initUpdateBillingActionsDetails(queryParams));
    } else {
      dispatch(actions.initBillingActionsDetails(queryParams));
    }
    setBusy(false);
    setEdit(false);
    setRequestCompleted(undefined);
    return () => {};
  });

  //USE EFFECT
  useEffect(() => {
    let active = loading === false;
    if (active) {
      if (queryParams.id !== undefined && !isNaN(parseInt(queryParams.id))) {
        setEdit(true);
      }
      if (requestCreateCompleted === true || requestUpdateCompleted === true) {
        setRequestCompleted(true);
        setBusy(false);
      } else if (
        requestCreateCompleted === false ||
        requestUpdateCompleted === false
      ) {
        setRequestCompleted(false);
        setBusy(false);
      }
      if (requestUpdateCompleted === true || requestCreateCompleted === true) {
        handleCancelClick();
      }
    }
    return () => {
      active = false;
    };
  }, [
    handleCancelClick,
    loading,
    queryParams.id,
    requestCreateCompleted,
    requestUpdateCompleted,
  ]);

  //USE EFFECT 2
  useEffect(() => {
    if (confirmApproved) {
      if (edit) {
        dispatch(actions.resetUpdateBillingActionsDetailsState());
      } else {
        dispatch(actions.resetCreateBillingActionsDetailsState());
      }
      if (continueToOther.continueOnApprove) {
        dispatch(layoutActions.resetSidePanel());
        setTimeout(() => {
          dispatch(
            layoutActions.openSidePanel({
              type: continueToOther.pageType,
              props: continueToOther.pageProps,
              expanded: continueToOther.expanded,
            }),
          );
          dispatch(layoutActions.resetContinueState());
        }, 200);
      } else if (continueLink !== undefined) {
        dispatch(appSettingsActions.navigate(continueLink));
        dispatch(layoutActions.resetSidePanel());
      } else {
        dispatch(layoutActions.resetSidePanel());
      }
    }
    if (confirmRejected) {
      dispatch(layoutActions.resetConfirmState());
    }
    return () => undefined;
  }, [
    actions,
    appSettingsActions,
    confirmApproved,
    confirmRejected,
    continueLink,
    continueToOther,
    dispatch,
    edit,
    layoutActions,
  ]);

  //LEFT ACTIONS
  const leftActions = [
    () => (
      <Fragment>
        <SaveFormButton
          size="small"
          startIcon={<Icon icon="save" />}
          onClick={handleSaveClick}
          disabled={busy}
          processing={!requestCompleted && busy}
          edit={edit}
          text={t(translations.Save)}
        />
      </Fragment>
    ),
  ] as ActionRenderer[];

  //RIGHT ACTIONS
  const rightActions = [
    () => (
      <Fragment>
        <Button
          variant="ghost"
          size="small"
          startIcon={<Icon icon="times" />}
          onClick={handleCancelClick}
        >
          {t(translations.Cancel)}
        </Button>
      </Fragment>
    ),
  ] as ActionRenderer[];

  //RENDER
  return (
    <>
      <PageWrapper
        id="billing_actions_details"
        pageName={
          edit
            ? t(translations.menu_EditAction)
            : t(translations.menu_AddNewAction)
        }
        titlePage={
          baseModel
            ? edit
              ? ` ${t(translations.EditAction)} #${queryParams.id || 'N/A'}`
              : ` ${t(translations.AddNewAction)}`
            : undefined
        }
        loading={loading}
        useSidePanel={useSidePanel}
        leftActions={leftActions}
        rightActions={rightActions}
        closable={true}
        closeSidePanel={handleClose}
        toPageLink={`BillingActions/details?${toQueryString(
          props.queryParams,
        )}`}
        pageLink={buildURL(
          publicUrl + 'BillingActions/details',
          props.queryParams,
        )}
        children={
          baseModel !== undefined && !loading ? (
            <Fragment>
              <BillingActionsForm
                initialValues={baseModel || ({} as BillingActionsDetailsState)}
                bindSubmitForm={bindSubmitForm}
                onSubmit={handleSubmit}
                isEdit={edit}
                isAdmin={IsAdmin}
              />
            </Fragment>
          ) : (
            <Fragment>
              {' '}
              <Box component="div">
                <Progress inProgress={loading} size={80} />
              </Box>
            </Fragment>
          )
        }
      />
      {hasUnsavedChanges && (
        <Beforeunload onBeforeunload={() => 'Youll lose your data!'} />
      )}
    </>
  );
}
